/**
 * Shared helpers for rules that walk `ctx.graph` directly (vs. bubble
 * aggregates). Keeps rule files focused on the decision logic, not
 * prop plumbing.
 */

import type { PersistedNode, PersistedEdge } from '../../../../models/KnowledgeGraph';

export function propsOf(node: PersistedNode): Record<string, unknown> {
  return (node.props ?? {}) as Record<string, unknown>;
}

export function manifestIdOf(node: PersistedNode): string {
  const p = propsOf(node);
  const mid = (p._manifestId as string | undefined) ?? (p.manifestId as string | undefined);
  if (mid) return String(mid);
  return node.type;
}

export function isOneOfService(node: PersistedNode, services: readonly string[]): boolean {
  const m = manifestIdOf(node).toLowerCase();
  return services.some((s) => m === s || m.includes(s));
}

/** Boolean extractor tolerant of true/false/'true'/'false'. */
export function boolProp(
  root: Record<string, unknown> | undefined,
  path: readonly string[],
): boolean | undefined {
  let cur: unknown = root;
  for (const k of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  if (typeof cur === 'boolean') return cur;
  if (cur === 'true') return true;
  if (cur === 'false') return false;
  return undefined;
}

export function strProp(
  root: Record<string, unknown> | undefined,
  path: readonly string[],
): string | undefined {
  let cur: unknown = root;
  for (const k of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  return typeof cur === 'string' ? cur : undefined;
}

export function arrProp(
  root: Record<string, unknown> | undefined,
  path: readonly string[],
): unknown[] | undefined {
  let cur: unknown = root;
  for (const k of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  return Array.isArray(cur) ? cur : undefined;
}

/**
 * Resource "is public" check based on the shared CloudResource shape:
 *   access.publicExposure in ('restricted' | 'open')
 */
export function publicExposure(
  node: PersistedNode,
): 'none' | 'restricted' | 'open' | undefined {
  const val = strProp(propsOf(node), ['access', 'publicExposure']);
  if (val === 'none' || val === 'restricted' || val === 'open') return val;
  return undefined;
}

/** Best-effort AZ extraction for single-AZ rule. */
export function azList(node: PersistedNode): string[] {
  const p = propsOf(node);
  const single = p.az as string | undefined;
  if (single) return [single];
  const multi = arrProp(p, ['availabilityZones']) ?? arrProp(p, ['azs']);
  if (multi) return multi.filter((x): x is string => typeof x === 'string');
  const sgs = arrProp(p, ['subnets']) ?? [];
  const out = new Set<string>();
  for (const s of sgs) {
    if (s && typeof s === 'object' && typeof (s as { az?: unknown }).az === 'string') {
      out.add(String((s as { az: string }).az));
    }
  }
  return Array.from(out);
}

export function iterEdgesOfKind(
  edges: readonly PersistedEdge[],
  kinds: readonly string[],
): PersistedEdge[] {
  const set = new Set(kinds);
  return edges.filter((e) => set.has(e.type));
}

/** Parse ISO timestamp tolerantly. Returns NaN for missing/malformed. */
export function toMillis(s: string | undefined): number {
  if (!s) return NaN;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : NaN;
}
